/**
 * @fileoverview Financial Year filter dropdown for a banking page's header filters row.
 * @description Wraps the shared `Select` atom with the banking module's own Year-filter option
 * list — "5 Years" plus each fiscal year (the latest reading as "Current FY", every other year in
 * the bank's own "FY YY-YY" range notation) — so every banking page gets this exact control without
 * re-deriving the option list itself. Ported from the InsightBox Banking design system's own
 * `PageFilterBar.tsx` (its `FY_OPTIONS` construction), split out as its own small component since
 * this app's convention is a `filters` slot on `PageHeader` built per-page from smaller pieces,
 * rather than one monolithic filter-bar component owning the whole heading row.
 * @copyright Copyright (c) 2026 Brownbox Consulting. All rights reserved.
 * @author Rahul Chawda
 */

import { useTranslation } from 'react-i18next'

import {
  FISCAL_YEARS,
  FIVE_YEAR_ID,
  fyRangeLabel,
} from '@/features/banking/constants/banking-options.constants'
import Select from '@/shared/components/atoms/Select'

import type { FyFilter } from '@/features/banking/constants/banking-options.constants'

interface FyFilterSelectProps {
  value: FyFilter
  onChange: (value: FyFilter) => void
}

/**
 * @component
 * @param {FyFilterSelectProps} props - The control's current value and change handler.
 * @param {FyFilter} props.value - The currently-selected Year filter value.
 * @param {(value: FyFilter) => void} props.onChange - Called with the newly-selected value.
 */
function FyFilterSelect({ value, onChange }: FyFilterSelectProps) {
  const { t } = useTranslation()
  const options = [
    { value: FIVE_YEAR_ID, label: t('banking.filters.fiveYears') },
    // The most recent year reads as "Current FY" rather than its own range label — every other
    // (past) year uses the FY YY-YY notation.
    ...[...FISCAL_YEARS].reverse().map((fy, index) => ({
      value: fy,
      label: index === 0 ? t('banking.filters.currentFy') : fyRangeLabel(fy),
    })),
  ]

  return (
    <div className="flex flex-col gap-1.5">
      <span className="text-xs font-medium text-slate">{t('banking.filters.financialYear')}</span>
      <Select
        value={value}
        onChange={(event) => onChange(event.target.value as FyFilter)}
        options={options}
      />
    </div>
  )
}

export default FyFilterSelect
